import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ILocation } from '@clickersphere/core/entities/ILocation';
import { useCreateLocation } from '../../../app/hooks/location/useCreateLocation';
import { useDeleteLocation } from '../../../app/hooks/location/useDeleteLocation';
import { useLocations } from '../../../app/hooks/location/useLocations';
import { useUpdateLocation } from '../../../app/hooks/location/useUpdateLocation';
import useToast from '../../../app/hooks/useToast';
import {
  emptyContractAddressFormValues,
  IContractAddressFormValues,
  toContractAddressFormValues,
  toContractAddressPayload,
} from '../../../app/utils/contractAddressForm';

export interface ILocationFormValues {
  name: string;
  address: IContractAddressFormValues;
}

interface IUseLocationComboboxController {
  value?: string | null;
  onChange: (locationId: string | null, locationName?: string) => void;
}

export function useLocationComboboxController({
  value,
  onChange,
}: IUseLocationComboboxController) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { locations, isLocationsLoading } = useLocations();
  const { createLocationAsync, createLocationIsPending } = useCreateLocation();
  const { updateLocationAsync, updateLocationIsPending } = useUpdateLocation();
  const { deleteLocationAsync, deleteLocationIsPending } = useDeleteLocation();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [editingLocationId, setEditingLocationId] = useState<string | null>(
    null,
  );
  const [showForm, setShowForm] = useState(false);
  const [isAddressVisible, setIsAddressVisible] = useState(false);
  const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);

  const form = useForm<ILocationFormValues>({
    defaultValues: { name: '', address: emptyContractAddressFormValues },
  });

  const isEditing = editingLocationId !== null;
  const isFormBusy =
    createLocationIsPending ||
    updateLocationIsPending ||
    deleteLocationIsPending;

  const selectedLocation = useMemo(
    () => locations.find((location) => location.id === value) ?? null,
    [locations, value],
  );

  const filteredLocations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return locations;
    return locations.filter((location) =>
      location.name.toLowerCase().includes(query),
    );
  }, [locations, search]);

  const exactMatch = useMemo(
    () =>
      locations.some(
        (location) =>
          location.name.toLowerCase() === search.trim().toLowerCase(),
      ),
    [locations, search],
  );

  function resetForm() {
    setShowForm(false);
    setEditingLocationId(null);
    setIsAddressVisible(false);
    setIsDeleteConfirming(false);
    form.reset({ name: '', address: emptyContractAddressFormValues });
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      resetForm();
      setSearch('');
    }
  }

  function handleSelect(location: ILocation) {
    onChange(location.id, location.name);
    handleOpenChange(false);
  }

  function handleClear() {
    onChange(null, undefined);
  }

  function openCreateForm() {
    setEditingLocationId(null);
    setIsAddressVisible(false);
    setIsDeleteConfirming(false);
    form.reset({
      name: search.trim(),
      address: emptyContractAddressFormValues,
    });
    setShowForm(true);
  }

  function openEditForm(location: ILocation) {
    setEditingLocationId(location.id);
    setIsDeleteConfirming(false);
    const address = toContractAddressFormValues(location.address);
    setIsAddressVisible(
      Object.values(address).some((field) => field.trim() !== ''),
    );
    form.reset({ name: location.name, address });
    setShowForm(true);
  }

  function toggleAddressVisibility() {
    setIsAddressVisible((previous) => !previous);
  }

  const handleSubmit = form.handleSubmit(async (values) => {
    const name = values.name.trim();
    if (!name) return;

    const address = toContractAddressPayload(values.address) ?? null;

    try {
      if (editingLocationId) {
        await updateLocationAsync({
          id: editingLocationId,
          data: { name, address },
        });
        toast(t('location.toast.updated'), 'success');
        if (editingLocationId === value) {
          onChange(editingLocationId, name);
        }
      } else {
        const created = await createLocationAsync({ name, address });
        toast(t('location.toast.created'), 'success');
        onChange(created.id, created.name);
        setSearch('');
        handleOpenChange(false);
        return;
      }
      resetForm();
    } catch {
      toast(
        editingLocationId
          ? t('location.toast.error.update')
          : t('location.toast.error.create'),
        'error',
      );
    }
  });

  async function handleDelete() {
    if (!editingLocationId) return;

    if (!isDeleteConfirming) {
      setIsDeleteConfirming(true);
      return;
    }

    try {
      await deleteLocationAsync(editingLocationId);
      toast(t('location.toast.deleted'), 'success');
      if (editingLocationId === value) {
        onChange(null, undefined);
      }
      resetForm();
    } catch {
      toast(t('location.toast.error.delete'), 'error');
    }
  }

  return {
    t,
    form,
    open,
    search,
    showForm,
    isEditing,
    isFormBusy,
    isAddressVisible,
    isDeleteConfirming,
    isLocationsLoading,
    selectedLocation,
    filteredLocations,
    exactMatch,
    setSearch,
    handleOpenChange,
    handleSelect,
    handleClear,
    openCreateForm,
    openEditForm,
    toggleAddressVisibility,
    toggleDeleteConfirm: handleDelete,
    handleSubmit,
    resetForm,
  };
}
